/**
 * Onboarding Tour — 5-stage progress tracker.
 *
 * Replaces the legacy 8-slide `AlertsOnboardingModal` slideshow with a
 * stage-based hub. Each stage maps to an existing flow in the product:
 *
 *   1. questionnaire — `OnboardingTypeform` (signup survey)
 *   2. research     — `/replay/market_edge_scan_setups` (research replay)
 *   3. alert        — `/alerts/<demo>?onboarding=1` (alert page walkthrough)
 *   4. automations  — `/automations?onboarding_automations=1` (signals page)
 *   5. guides       — `/blog?onboarding_blog=1` (guides page + article tour)
 *
 * Persistence: client-side localStorage keyed by uid. Simplest possible
 * model — no backend round-trip, no cross-device sync (acceptable for v1
 * by explicit product call). Cross-component updates flow via a custom
 * window event so any mounted hub / card flips instantly without a poll.
 *
 * Auto-open dismissal lives in sessionStorage so it resets next session.
 */

import { useEffect, useState, useCallback } from "react";
import {
  ONBOARDING_DEMO_CURATED_ID,
  ONBOARDING_START_QUERY_PARAM,
  ONBOARDING_START_QUERY_VALUE,
  AUTOMATIONS_TOUR_QUERY_PARAM,
  AUTOMATIONS_TOUR_QUERY_VALUE,
  BLOG_TOUR_QUERY_PARAM,
  BLOG_TOUR_QUERY_VALUE,
} from "./onboarding-constants";

// ── Stage-3 deep-link into the curated onboarding replay ────────────────
// The "Build your first alert" stage drops the viewer into turn 1 of
// the same curated replay — skipping the research turn the viewer
// already watched in stage 2. Implemented by passing two URL params:
//
//   start_at_beat=alert_welcome   — backend pre-releases turn 0 beats and
//                                   seeds `current_turn=1` on first request
//                                   (see `app/api/routes/replay.py`).
//   initial_prompt=<copy>         — frontend renders THIS text as the first
//                                   user bubble instead of the recorded
//                                   handoff prompt, so the question reads
//                                   as a standalone alert-building request
//                                   rather than a follow-up to research
//                                   the viewer never saw.
const STAGE_3_START_BEAT = "alert_welcome";
const STAGE_3_INITIAL_PROMPT =
  "Build me a daily workflow that scans the S&P 500 for the highest-conviction setup of the day. Pull options flow, sentiment, fundamentals, GEX, and technicals, score each name, then email me a card with the top setup and the trade plan before the open.";

// ── Stage definitions ────────────────────────────────────────────────────
export type TourStageId =
  | "questionnaire"
  | "research"
  | "alert"
  | "automations"
  | "guides"
  | "journey";

export interface TourStageDef {
  id: TourStageId;
  index: number; // 1-based position in the tour
  title: string;
  blurb: string;
  /** Estimated time copy shown under the title. */
  duration: string;
  /** Route to push when the user clicks Start / Resume / Replay. */
  href: string;
}

export const TOUR_STAGES: readonly TourStageDef[] = [
  {
    id: "questionnaire",
    index: 1,
    title: "Set up your profile",
    blurb: "Tell Xynth your style and goals.",
    duration: "1 min",
    href: `/?${ONBOARDING_START_QUERY_PARAM}=${ONBOARDING_START_QUERY_VALUE}`,
  },
  {
    id: "research",
    index: 2,
    title: "Run live AI research",
    blurb: "Watch Xynth find a trade idea live.",
    duration: "3 min",
    href: `/replay/${ONBOARDING_DEMO_CURATED_ID}`,
  },
  {
    id: "alert",
    index: 3,
    title: "Build your first alert",
    blurb: "Turn research into a scheduled alert.",
    duration: "3 min",
    // Re-uses the SAME curated replay as stage 2 but jumps straight to
    // turn 1 (the alert-building turn) via `start_at_beat`, with a
    // rewritten standalone first prompt so the question doesn't read
    // as a follow-up to the research the viewer is skipping past.
    href: `/replay/${ONBOARDING_DEMO_CURATED_ID}?start_at_beat=${STAGE_3_START_BEAT}&initial_prompt=${encodeURIComponent(STAGE_3_INITIAL_PROMPT)}`,
  },
  {
    id: "automations",
    index: 4,
    title: "Subscribe to signals",
    blurb: "Browse the automations marketplace.",
    duration: "2 min",
    href: `/automations?${AUTOMATIONS_TOUR_QUERY_PARAM}=${AUTOMATIONS_TOUR_QUERY_VALUE}`,
  },
  {
    id: "guides",
    index: 5,
    title: "Read the guides",
    blurb: "Tactical breakdowns from the Xynth team.",
    duration: "2 min",
    href: `/blog?${BLOG_TOUR_QUERY_PARAM}=${BLOG_TOUR_QUERY_VALUE}`,
  },
  // Final stage shown ONLY to users who haven't started a free trial or
  // active subscription yet. Filtered at render time in
  // `OnboardingTourHub` via `useUserUsage` — kept in TOUR_STAGES so
  // progress storage/types stay uniform.
  {
    id: "journey",
    index: 6,
    title: "Start your journey",
    blurb: "See what Xynth can do for you.",
    duration: "1 min",
    href: `/?onboarding_home=finale`,
  },
] as const;

// The `journey` stage is an OPTIONAL conversion-CTA row that only
// appears for users who haven't started a free trial / subscription.
// It deliberately does NOT count toward "tour complete" — the legacy
// 5-stage tour-complete contract (used by AlertsOnboardingGate /
// AutomationsTutorialCard) stays at 5 so subscribed users can still
// reach 100%. Hub rendering uses its own visible-stage count.
export const CORE_TOUR_STAGE_IDS: readonly TourStageId[] = [
  "questionnaire",
  "research",
  "alert",
  "automations",
  "guides",
] as const;

export const TOUR_TOTAL = CORE_TOUR_STAGE_IDS.length;

// ── Storage helpers ──────────────────────────────────────────────────────
const LS_PROGRESS_PREFIX = "xynth.tour_progress:";
const SS_DISMISSED_PREFIX = "xynth.tour_dismissed:";
// Per-uid "has the hub auto-opened at least once for this user?" flag.
// Set the first time the post-rollout introduction auto-opens the hub
// AND the user closes it; never auto-opens again for that uid afterwards.
// Manual opens (clicking the Onboarding tile on /automations) ignore this
// flag entirely — the flag is only consulted by the auto-open gate in
// AlertsOnboardingGate.
const LS_HUB_INTRODUCED_PREFIX = "xynth.tour_hub_introduced:";

export function hasSeenTourHubIntro(uid: string | null | undefined): boolean {
  if (!uid || typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(`${LS_HUB_INTRODUCED_PREFIX}${uid}`) === "true";
  } catch {
    // Private mode / quota — fail closed (treat as already-seen so we
    // never repeat-pop the modal on every page load if storage breaks).
    return true;
  }
}

export function markTourHubIntroSeen(uid: string | null | undefined): void {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${LS_HUB_INTRODUCED_PREFIX}${uid}`, "true");
  } catch {
    /* private mode / quota — ignore. */
  }
}

export type TourProgress = Record<TourStageId, boolean>;

const EMPTY_PROGRESS: TourProgress = {
  questionnaire: false,
  research: false,
  alert: false,
  automations: false,
  guides: false,
  journey: false,
};

function lsKey(uid: string): string {
  return `${LS_PROGRESS_PREFIX}${uid}`;
}

function ssKey(uid: string): string {
  return `${SS_DISMISSED_PREFIX}${uid}`;
}

export function readTourProgress(uid: string | null | undefined): TourProgress {
  if (!uid || typeof window === "undefined") return { ...EMPTY_PROGRESS };
  try {
    const raw = window.localStorage.getItem(lsKey(uid));
    if (!raw) return { ...EMPTY_PROGRESS };
    const parsed = JSON.parse(raw);
    return {
      questionnaire: Boolean(parsed?.questionnaire),
      research: Boolean(parsed?.research),
      alert: Boolean(parsed?.alert),
      automations: Boolean(parsed?.automations),
      guides: Boolean(parsed?.guides),
      journey: Boolean(parsed?.journey),
    };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

function writeTourProgress(uid: string, next: TourProgress): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(lsKey(uid), JSON.stringify(next));
  } catch {
    /* private mode / quota — ignore. */
  }
}

// ── Event bus ────────────────────────────────────────────────────────────
// Fired any time progress mutates so mounted hub / card components can
// flip state without polling.
export const TOUR_PROGRESS_UPDATED_EVENT = "xynth:tour-progress-updated";

// Fired by tour finishers (replay end, buddy tour last beat, …) to mark
// the corresponding stage complete. Centralising on a window event keeps
// each tour file decoupled from the storage layer.
export const TOUR_STAGE_COMPLETE_EVENT = "xynth:tour-stage-complete";

export interface TourStageCompleteDetail {
  stage: TourStageId;
}

export function dispatchStageComplete(stage: TourStageId): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<TourStageCompleteDetail>(TOUR_STAGE_COMPLETE_EVENT, {
      detail: { stage },
    }),
  );
}

// ── Mutators ─────────────────────────────────────────────────────────────
export function markTourStageComplete(
  uid: string | null | undefined,
  stage: TourStageId,
): void {
  if (!uid) return;
  const current = readTourProgress(uid);
  if (current[stage]) return; // idempotent
  const next: TourProgress = { ...current, [stage]: true };
  writeTourProgress(uid, next);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TOUR_PROGRESS_UPDATED_EVENT, { detail: next }),
    );
  }
}

export function resetTourProgress(uid: string | null | undefined): void {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(lsKey(uid));
    window.sessionStorage.removeItem(ssKey(uid));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent(TOUR_PROGRESS_UPDATED_EVENT, { detail: EMPTY_PROGRESS }),
  );
}

// ── Auto-open dismissal (session-scoped) ─────────────────────────────────
export function isTourDismissedThisSession(
  uid: string | null | undefined,
): boolean {
  if (!uid || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(ssKey(uid)) === "1";
  } catch {
    return false;
  }
}

export function dismissTourThisSession(uid: string | null | undefined): void {
  if (!uid || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ssKey(uid), "1");
  } catch {
    /* ignore */
  }
}

// ── React hook ───────────────────────────────────────────────────────────
export interface UseTourProgressResult {
  progress: TourProgress;
  completedCount: number;
  total: number;
  allComplete: boolean;
  nextStage: TourStageDef | null; // first incomplete stage, or null when done
  markComplete: (stage: TourStageId) => void;
  reset: () => void;
}

export function useTourProgress(
  uid: string | null | undefined,
): UseTourProgressResult {
  const [progress, setProgress] = useState<TourProgress>(() =>
    readTourProgress(uid),
  );

  // Refresh when uid changes.
  useEffect(() => {
    setProgress(readTourProgress(uid));
  }, [uid]);

  // Subscribe to in-tab progress events.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent<TourProgress>).detail;
      if (detail) {
        setProgress(detail);
      } else {
        setProgress(readTourProgress(uid));
      }
    };
    const onStageComplete = (e: Event) => {
      const detail = (e as CustomEvent<TourStageCompleteDetail>).detail;
      if (detail?.stage) markTourStageComplete(uid, detail.stage);
    };
    // Cross-tab sync via storage event.
    const onStorage = (e: StorageEvent) => {
      if (!uid) return;
      if (e.key === lsKey(uid)) setProgress(readTourProgress(uid));
    };
    window.addEventListener(TOUR_PROGRESS_UPDATED_EVENT, onUpdate);
    window.addEventListener(TOUR_STAGE_COMPLETE_EVENT, onStageComplete);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(TOUR_PROGRESS_UPDATED_EVENT, onUpdate);
      window.removeEventListener(TOUR_STAGE_COMPLETE_EVENT, onStageComplete);
      window.removeEventListener("storage", onStorage);
    };
  }, [uid]);

  // Counts EXCLUDE the optional `journey` stage so the legacy
  // 5-stage completion contract (gate / tutorial card) is preserved.
  // The hub itself overrides these via `visibleStages` when it needs
  // to count the journey row toward its own progress bar.
  const completedCount = CORE_TOUR_STAGE_IDS.reduce(
    (acc, id) => acc + (progress[id] ? 1 : 0),
    0,
  );
  const allComplete = completedCount === TOUR_TOTAL;
  const nextStage =
    TOUR_STAGES.find(
      (s) => CORE_TOUR_STAGE_IDS.includes(s.id) && !progress[s.id],
    ) ?? null;

  const markComplete = useCallback(
    (stage: TourStageId) => markTourStageComplete(uid, stage),
    [uid],
  );
  const reset = useCallback(() => resetTourProgress(uid), [uid]);

  return {
    progress,
    completedCount,
    total: TOUR_TOTAL,
    allComplete,
    nextStage,
    markComplete,
    reset,
  };
}
